import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Path, UseFormRegisterReturn } from 'react-hook-form';
import { companiesEditSchema } from '../schemas/companiesEditSchema';
import type { SharedCustomData } from '@/types/serializers';
import { TextField } from '@/components/TextField';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import { Textarea } from '@/components/Textarea';
import Select from '@/components/Select';
import { useAtom } from 'jotai';
import { isCompanyProfileEditAtom } from '../atom/companiesAtom';
import clsx from 'clsx';
import useUpdateCompanyProfile from '../api/useUpdateCompanyProfile';
import { Button } from '@/components/Button';
import { MdOutlineEdit, MdOutlineFileDownload } from 'react-icons/md';
import CompaniesImportModal from './CompaniesImportModal';
import type { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import type {
  AgentApiCompaniesShow,
  AgentApiCompaniesCompanyContract,
  AgentApiCompaniesInformation,
  SharedCustomField,
} from '@/types/serializers';
import useFetchIndustries from '../../api/useFetchIndustries';
import { LISTED_PHASE_ITEMS } from '../constants';
import MultiSelect from '../../candidates/components/MultiSelect';
import { renderTextWithLinks } from '../../../../utils/textWithLinks';
import { normalizeCustomFieldsByCategory } from '../../../../utils/normalizeCustomFieldsByCategory';
import { buildCustomDataPayload } from '@/utils/customDataForm';

// 入力文字列から数字以外を除去する。type="number" は IME（日本語変換）を防げないため、
// type="text" + inputMode にしたうえで入力値そのものをサニタイズして数字だけ残す。
const sanitizeNumeric = (value: string, allowDecimal: boolean) => {
  if (!allowDecimal) return value.replace(/\D/g, '');
  // 数字とドット以外を除去し、ドットは最初の1つだけ残す（`1..2` → `1.2`）
  const cleaned = value.replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
};

// 文字列を数値へ変換する。空文字や `.` 単体など NaN になる入力は undefined を返す
const toNumberOrUndefined = (value?: string) => {
  if (!value) return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
};

const CONTRACT_STATUS_ITEMS = [
  { value: 'contracted', label: '契約済' },
  { value: 'nothing', label: '契約なし' },
  { value: 'other', label: 'その他' },
];

type FormValues = {
  customData: Record<string, string>;
  industry_id: string;
  description: string;
  official_url: string;
  address: string;
  number_of_employees: string;
  established_date: string;
  president_name: string;
  listed_phase: string;
  contract_status: string;
  fee_rate: string;
  minimum_fee: string;
  note: string;
};

type CompanyProfileDataProps = {
  label: string;
  text: string;
  isEditing: boolean;
  register: (name: string) => UseFormRegisterReturn;
  name: string;
  fieldType:
    | 'number'
    | 'short_text'
    | 'long_text'
    | 'single_select'
    | 'multiple_select'
    | 'date'
    | 'datetime';
  options?: { id: number; value: string; label?: string }[];
  watch: (name: string) => string;
  unit?: string;
  error?: string;
  required?: boolean;
};

const CompanyProfileData = ({
  label,
  text,
  isEditing,
  register,
  name,
  fieldType,
  options,
  watch,
  unit,
  error,
  required,
}: CompanyProfileDataProps & { watch: (name: string) => string }) => {
  const getDisplayText = () => {
    if (!text) return '';

    if (fieldType === 'single_select' && options) {
      const option = options.find((opt) => opt.value === text);
      return option?.label || text;
    }

    if (fieldType === 'multiple_select' && options) {
      const selectedValues = text
        .split(/[,/]/)
        .map((v) => v.trim())
        .filter((v) => v);
      const selectedLabels = selectedValues.map((value) => {
        const option = options.find((opt) => opt.value === value);
        return option?.label || value;
      });
      return selectedLabels.join(', ');
    }

    return text;
  };

  const displayText = getDisplayText();
  const renderInput = () => {
    switch (fieldType) {
      case 'long_text': {
        return <Textarea rows={4} {...register(name)} className="p-3" />;
      }
      case 'date': {
        return (
          <input
            type="date"
            className="h-[42px] rounded border border-solid border-border px-3 py-2 text-sm focus:border-2 focus:border-primary focus-visible:outline-hidden"
            {...register(name)}
          />
        );
      }
      case 'datetime': {
        return (
          <input
            type="datetime-local"
            className="h-[42px] rounded border border-solid border-border px-3 py-2 text-sm focus:border-2 focus:border-primary focus-visible:outline-hidden"
            {...register(name)}
          />
        );
      }
      case 'single_select': {
        const items =
          options?.map((o) => {
            return {
              value: o.value,
              label: o.label || o.value,
            };
          }) ?? [];

        const currentValue = watch(name) || text || '';

        return (
          <Select
            value={currentValue}
            onValueChange={(value) => {
              const event = {
                target: {
                  name,
                  value,
                },
              };
              register(name).onChange(event);
            }}
            items={items}
            defaultLabel="選択してください"
          />
        );
      }
      case 'multiple_select': {
        const currentValue = watch(name) || text || '';
        return (
          <MultiSelect
            customFieldOptions={options || []}
            initialValues={currentValue}
            onChange={(selectedOptions) => {
              const selectedValues = selectedOptions.map((option) => option.value).join(',');
              const event = {
                target: {
                  name,
                  value: selectedValues,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              register(name).onChange(event);
            }}
          />
        );
      }
      case 'number': {
        const reg = register(name);
        // 手数料(fee_rate)のみ小数を許可、それ以外（従業員数・ミニマムフィー・カスタム数値）は整数のみ
        const allowDecimal = name === 'fee_rate';
        return (
          <div className="relative">
            <TextField
              type="text"
              inputMode={allowDecimal ? 'decimal' : 'numeric'}
              error={!!error}
              {...reg}
              onChange={(e) => {
                e.target.value = sanitizeNumeric(e.target.value, allowDecimal);
                reg.onChange(e);
              }}
            />
            {unit && (
              <span className="absolute top-1/2 right-3 -translate-y-1/2 text-sm text-gray-500">
                {unit}
              </span>
            )}
          </div>
        );
      }
      default: {
        // NOTE: short_text
        return <TextField type="text" error={!!error} {...register(name)} />;
      }
    }
  };

  return (
    <div
      className={`flex w-full flex-col gap-xs ${fieldType === 'multiple_select' && isEditing ? '' : 'overflow-hidden'}`}
    >
      <p className="text-heading-xxs">
        {label}
        {required && (
          <span className="ml-2 inline-flex h-5 items-center rounded-full bg-danger_lighten px-3 text-xs leading-none font-bold text-danger">
            必須
          </span>
        )}
      </p>
      {isEditing ? (
        <div>
          {renderInput()}
          {error && (
            <p role="alert" className="mt-xxs text-body-xs text-danger">
              {error}
            </p>
          )}
        </div>
      ) : (
        <p
          className={clsx('text-sm leading-[170%] break-words', !displayText && 'text-placeholder')}
        >
          {displayText ? (
            <>
              {renderTextWithLinks(displayText)}
              {unit && <span className="ml-1 text-gray-500">{unit}</span>}
            </>
          ) : (
            '未設定'
          )}
        </p>
      )}
    </div>
  );
};

type Props = {
  companyId: number;
  companyName: string;
  customData: SharedCustomData[];
  organizationUserIds: number[];
  companyInformation: AgentApiCompaniesInformation | null;
  suggestionInfo: AgentApiCompaniesInformation | null;
  companyContract: AgentApiCompaniesCompanyContract | null;
  customFields: SharedCustomField[];
  companyRefetch: () => void | Promise<unknown>;
};

export default function CompaniesProfileCard({
  companyId,
  companyName,
  customData,
  organizationUserIds,
  companyInformation,
  suggestionInfo,
  companyContract,
  customFields,
  companyRefetch,
}: Props) {
  const { data: industries } = useFetchIndustries();
  const [name, setName] = useState(companyName);
  const [userIds, setUserIds] = useState(organizationUserIds);
  const [isCompanyProfileEdit, setIsCompanyProfileEdit] = useAtom(isCompanyProfileEditAtom);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const getIndustryIdByName = (name: string | null) => {
    if (!name || !industries) return '';
    const industry = industries.find((ind) => ind.name === name);
    return industry ? industry.id.toString() : '';
  };

  const getListedPhaseValue = (label: string | null | undefined) => {
    if (!label) return '';
    const item = LISTED_PHASE_ITEMS.find((item) => item.label === label);
    return item ? item.value : '';
  };

  const getContractStatusValue = (labelOrValue: string | null | undefined) => {
    if (!labelOrValue) return '';
    const itemByLabel = CONTRACT_STATUS_ITEMS.find((item) => item.label === labelOrValue);
    if (itemByLabel) return itemByLabel.value;
    return labelOrValue;
  };

  // 既存 custom_data の値 + 未入力フィールドの空文字で customData ネストオブジェクトを構築する。
  // 全フィールドにキーを持たせることで、'update' 送信時に空文字（明示削除）も含めて全件送信される
  const buildCustomDataDefaults = () => {
    // 既存 custom_data の id 集合を 1 回だけ構築し、未入力フィールドの判定を O(1) にする（全体で O(N+M)）
    const existingFieldIds = new Set(customData?.map((data) => data.custom_field.id));
    return {
      ...customData?.reduce(
        (acc, data) => {
          acc[String(data.custom_field.id)] = data.content;
          return acc;
        },
        {} as Record<string, string>
      ),
      ...customFields?.reduce(
        (acc, field) => {
          if (!existingFieldIds.has(field.id)) {
            acc[String(field.id)] = '';
          }
          return acc;
        },
        {} as Record<string, string>
      ),
    };
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(companiesEditSchema),
    // submit 前に不備が分かるよう、フォーカスアウト時に検証し、以降は入力のたびに再検証する
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      customData: buildCustomDataDefaults(),
      industry_id: getIndustryIdByName(companyInformation?.industry || null),
      description: companyInformation?.description || '',
      official_url: companyInformation?.official_url || '',
      address: companyInformation?.address || '',
      number_of_employees: companyInformation?.number_of_employees?.toString() || '',
      established_date: companyInformation?.established_date || '',
      president_name: companyInformation?.president_name || '',
      listed_phase: getListedPhaseValue(companyInformation?.listed_phase),
      contract_status: getContractStatusValue(companyContract?.contract_status) || '',
      fee_rate: companyContract?.fee_rate?.toString() || '',
      minimum_fee: companyContract?.minimum_fee?.toString() || '',
      note: companyContract?.note || '',
    },
  });
  const { mutateAsync: updateProfile } = useUpdateCompanyProfile();
  // CompanyProfileData は静的フィールドとカスタムフィールドの両方を string パスで受け取るため、
  // 型付き register/watch に委譲する小さなアダプタを介して渡す。
  // キャストは Path<FormValues> への局所的な 1 点に限定し、register/watch 本体の型チェックは維持する
  const registerField = (name: string) => register(name as Path<FormValues>);
  const watchField = (name: string) => watch(name as Path<FormValues>) as string;
  // 静的フィールドのエラーメッセージを文字列で取得する小さなアダプタ
  const getError = (name: string) =>
    (errors[name as keyof FormValues]?.message as string | undefined) ?? undefined;

  // 会社名(name)は RHF 管理外のため、ここで必須チェックする
  const isNameEmpty = name.trim() === '';
  // 会社名 + RHF の検証エラーが両方無いときだけ保存可能。
  // 事前入力済みの編集フォームでは isValid が初期 false のままになりやすいため、
  // errors の有無で判定する（編集モード開始時に trigger() で errors を初期化する）。
  const isFormValid = Object.keys(errors).length === 0 && !isNameEmpty;

  // クライアント検証に失敗した場合のフィードバック（作成フォームと同挙動）
  const onInvalid = () => {
    showErrorToast('入力内容に不備があります。エラー表示のある項目を確認してください');
    requestAnimationFrame(() => {
      const firstError = document.querySelector('[role="alert"]');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const onInputName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
  };

  const onSubmit = async (data: FormValues) => {
    // 会社名は RHF 管理外のため handleSubmit の検証を通り抜ける。ここで明示的にガードする
    if (name.trim() === '') {
      onInvalid();
      return;
    }

    const customDataReq = buildCustomDataPayload(data.customData, 'update');

    const requestData = {
      companyId: companyId,
      name: name,
      custom_data: customDataReq,
      organization_user_ids: userIds,
      company_information: {
        description: data.description,
        official_url: data.official_url,
        address: data.address,
        number_of_employees: data.number_of_employees ? Number(data.number_of_employees) : null,
        established_date: data.established_date,
        president_name: data.president_name,
        listed_phase: data.listed_phase as
          | 'unlisted'
          | 'prime_market'
          | 'standard_market'
          | 'growth_market'
          | 'jasdaq'
          | 'tokyo_pro'
          | 'unknown',
        industry_id: data.industry_id ? Number(data.industry_id) : undefined,
      },
      company_contract: {
        contract_status: data.contract_status
          ? (data.contract_status as 'contracted' | 'nothing' | 'other')
          : undefined,
        // `.` 単体など Number() で NaN になる入力は未指定として扱う
        fee_rate: toNumberOrUndefined(data.fee_rate),
        minimum_fee: toNumberOrUndefined(data.minimum_fee),
        note: data.note || undefined,
      },
    };

    try {
      await updateProfile(requestData);
      setIsCompanyProfileEdit(false);
      await companyRefetch();
      showSuccessToast('更新しました');
    } catch (error) {
      let errorMsg = '更新に失敗しました';
      if (error instanceof Error) {
        errorMsg = error.message;
      } else if (typeof error === 'string') {
        errorMsg = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMsg = String(error.message);
      }

      showErrorToast(errorMsg);
    }
  };

  // 配列の中身が変わった時のみ同期する（参照の変化による無限再レンダリングを防止）
  const organizationUserIdsJson = JSON.stringify(organizationUserIds);
  useEffect(() => {
    setUserIds(organizationUserIds);
  }, [organizationUserIdsJson]);

  useEffect(() => {
    if (isCompanyProfileEdit) {
      setName(companyName);
      // 編集モードに切り替えた時に、フォームの値をリセット
      reset({
        customData: buildCustomDataDefaults(),
        industry_id: getIndustryIdByName(companyInformation?.industry || null),
        description: companyInformation?.description || '',
        official_url: companyInformation?.official_url || '',
        address: companyInformation?.address || '',
        number_of_employees: companyInformation?.number_of_employees?.toString() || '',
        established_date: companyInformation?.established_date || '',
        president_name: companyInformation?.president_name || '',
        listed_phase: getListedPhaseValue(companyInformation?.listed_phase),
        contract_status: getContractStatusValue(companyContract?.contract_status) || '',
        fee_rate: companyContract?.fee_rate?.toString() || '',
        minimum_fee: companyContract?.minimum_fee?.toString() || '',
        note: companyContract?.note || '',
      });
      // 編集モード開始時点で検証を走らせ、保存ボタンの活性状態を正しく初期化する
      void trigger();
    }
  }, [
    isCompanyProfileEdit,
    companyName,
    companyInformation,
    companyContract,
    customData,
    customFields,
    industries,
    reset,
  ]);

  const companyInfoFields = [
    { name: 'description', label: '事業内容', fieldType: 'long_text' as const },
    { name: 'president_name', label: '代表者名', fieldType: 'short_text' as const },
    {
      name: 'listed_phase',
      label: '上場情報',
      fieldType: 'single_select' as const,
      options: LISTED_PHASE_ITEMS.map((item, index) => ({
        id: index + 1,
        value: item.value,
        label: item.label,
      })),
    },
    { name: 'established_date', label: '設立年', fieldType: 'date' as const },
    { name: 'number_of_employees', label: '従業員数', fieldType: 'number' as const },
    { name: 'address', label: '住所', fieldType: 'short_text' as const },
    { name: 'official_url', label: '公式URL', fieldType: 'short_text' as const },
    {
      name: 'industry_id',
      label: '業種',
      fieldType: 'single_select' as const,
      options:
        industries?.map((industry) => ({
          id: industry.id,
          value: industry.id.toString(),
          label: industry.name,
        })) || [],
    },
  ];

  const agentCustomFields = normalizeCustomFieldsByCategory(customFields);

  return (
    <div className="relative flex flex-col gap-6">
      <div className="bg-white px-10 pt-8 pb-9 shadow-[0px_0px_6px_0px_#00000033]">
        <div className="flex items-center gap-4">
          <p className="text-lg font-bold text-primary">企業情報</p>
          {!isCompanyProfileEdit && (
            <div className="w-[84px]">
              <Button className="h-xl text-xs" onClick={() => setIsCompanyProfileEdit(true)}>
                <MdOutlineEdit size={16} />
                編集する
              </Button>
            </div>
          )}
          {suggestionInfo && (
            <div className="ml-auto">
              <Button
                className="h-xl border-2 border-solid"
                color="secondary"
                onClick={() => setIsImportModalOpen(true)}
              >
                <MdOutlineFileDownload size={16} />
                企業情報をインポートする
              </Button>
            </div>
          )}
        </div>
        <div className="mt-6 flex items-center gap-6">
          <div className="flex items-center gap-4">
            {isCompanyProfileEdit ? (
              <div className="flex w-[508px] flex-col gap-xs">
                <label htmlFor="companyName" className="text-heading-xxs">
                  会社名
                  <span className="ml-2 inline-flex h-5 items-center rounded-full bg-danger_lighten px-3 text-xs leading-none font-bold text-danger">
                    必須
                  </span>
                </label>
                <div>
                  <TextField
                    name="companyName"
                    defaultValue={name}
                    onChange={onInputName}
                    error={isNameEmpty}
                  />
                  {isNameEmpty && (
                    <p role="alert" className="mt-xxs text-body-xs text-danger">
                      ＊会社名は必須項目です。入力してください。
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-xl font-bold">{companyName}</p>
            )}
          </div>
        </div>

        <div className="mt-7 flex flex-col gap-7">
          {isCompanyProfileEdit ? (
            <form
              onSubmit={handleSubmit(onSubmit, onInvalid)}
              className={isCompanyProfileEdit ? 'flex flex-col gap-y-md' : ''}
            >
              {(() => {
                let skipNext = false;
                return companyInfoFields.map((field, index) => {
                  if (skipNext) {
                    skipNext = false;
                    return null;
                  }

                  const fieldValue = (() => {
                    // industry_idの場合は、業種名からIDを取得
                    if (field.name === 'industry_id') {
                      return getIndustryIdByName(companyInformation?.industry || null);
                    }

                    // listed_phaseの場合は、ラベルから値に変換
                    if (field.name === 'listed_phase') {
                      return getListedPhaseValue(companyInformation?.listed_phase);
                    }

                    return (
                      (
                        companyInformation?.[field.name as keyof typeof companyInformation] as
                          string | number | null
                      )?.toString() || ''
                    );
                  })();

                  if ((field.fieldType as string) === 'long_text') {
                    return (
                      <div key={field.name} className="w-full">
                        <CompanyProfileData
                          label={field.label}
                          text={watchField(field.name) || fieldValue}
                          isEditing={isCompanyProfileEdit}
                          register={registerField}
                          name={field.name}
                          fieldType={field.fieldType}
                          options={field.options}
                          watch={watchField}
                          error={getError(field.name)}
                        />
                      </div>
                    );
                  } else {
                    const nextField =
                      index + 1 < companyInfoFields.length ? companyInfoFields[index + 1] : null;
                    const isNextShort =
                      nextField && (nextField.fieldType as string) !== 'long_text';

                    const nextFieldValue = nextField
                      ? (() => {
                          if (nextField.name === 'industry_id') {
                            return getIndustryIdByName(companyInformation?.industry || null);
                          }

                          // listed_phaseの場合は、ラベルから値に変換
                          if (nextField.name === 'listed_phase') {
                            return getListedPhaseValue(companyInformation?.listed_phase);
                          }

                          return (
                            (
                              companyInformation?.[
                                nextField.name as keyof typeof companyInformation
                              ] as string | number | null
                            )?.toString() || ''
                          );
                        })()
                      : '';

                    if (isNextShort) {
                      skipNext = true;
                    }

                    return (
                      <div
                        key={`row-${index}`}
                        className="grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-md"
                      >
                        <CompanyProfileData
                          label={field.label}
                          text={watchField(field.name) || fieldValue}
                          isEditing={isCompanyProfileEdit}
                          register={registerField}
                          name={field.name}
                          fieldType={field.fieldType}
                          options={field.options}
                          watch={watchField}
                          error={getError(field.name)}
                          required={field.name === 'address'}
                        />
                        {isNextShort && (
                          <CompanyProfileData
                            label={nextField.label}
                            text={watchField(nextField.name) || nextFieldValue}
                            isEditing={isCompanyProfileEdit}
                            register={registerField}
                            name={nextField.name}
                            fieldType={nextField.fieldType}
                            options={nextField.options}
                            watch={watchField}
                            error={getError(nextField.name)}
                            required={nextField.name === 'address'}
                          />
                        )}
                      </div>
                    );
                  }
                });
              })()}
            </form>
          ) : (
            <>
              {(() => {
                let skipNext = false;
                return companyInfoFields.map((field, index) => {
                  if (skipNext) {
                    skipNext = false;
                    return null;
                  }

                  const fieldValue = (() => {
                    // industry_idの場合は、業種名からIDを取得
                    if (field.name === 'industry_id') {
                      return getIndustryIdByName(companyInformation?.industry || null);
                    }

                    // listed_phaseの場合は、ラベルから値に変換（表示時に再度ラベルに変換される）
                    if (field.name === 'listed_phase') {
                      return getListedPhaseValue(companyInformation?.listed_phase);
                    }

                    return (
                      (
                        companyInformation?.[field.name as keyof typeof companyInformation] as
                          string | number | null
                      )?.toString() || ''
                    );
                  })();

                  if ((field.fieldType as string) === 'long_text') {
                    return (
                      <div key={field.name} className="w-full">
                        <CompanyProfileData
                          label={field.label}
                          text={fieldValue}
                          isEditing={isCompanyProfileEdit}
                          register={registerField}
                          name={field.name}
                          fieldType={field.fieldType}
                          options={field.options}
                          watch={watchField}
                        />
                      </div>
                    );
                  } else {
                    const nextField =
                      index + 1 < companyInfoFields.length ? companyInfoFields[index + 1] : null;
                    const isNextShort =
                      nextField && (nextField.fieldType as string) !== 'long_text';

                    const nextFieldValue = nextField
                      ? (() => {
                          // industry_idの場合は、業種名からIDを取得
                          if (nextField.name === 'industry_id') {
                            return getIndustryIdByName(companyInformation?.industry || null);
                          }

                          // listed_phaseの場合は、ラベルから値に変換（表示時に再度ラベルに変換される）
                          if (nextField.name === 'listed_phase') {
                            return getListedPhaseValue(companyInformation?.listed_phase);
                          }

                          return (
                            (
                              companyInformation?.[
                                nextField.name as keyof typeof companyInformation
                              ] as string | number | null
                            )?.toString() || ''
                          );
                        })()
                      : '';

                    if (isNextShort) {
                      skipNext = true;
                    }

                    return (
                      <div key={`row-${index}`} className="flex gap-x-[30px]">
                        <CompanyProfileData
                          label={field.label}
                          text={fieldValue}
                          isEditing={isCompanyProfileEdit}
                          register={registerField}
                          name={field.name}
                          fieldType={field.fieldType}
                          options={field.options}
                          watch={watchField}
                        />
                        {isNextShort && (
                          <CompanyProfileData
                            label={nextField.label}
                            text={nextFieldValue}
                            isEditing={isCompanyProfileEdit}
                            register={registerField}
                            name={nextField.name}
                            fieldType={nextField.fieldType}
                            options={nextField.options}
                            watch={watchField}
                          />
                        )}
                      </div>
                    );
                  }
                });
              })()}
            </>
          )}
        </div>
      </div>

      {/* 契約情報セクション */}
      <div className="bg-white px-10 pt-8 pb-9 shadow-[0px_0px_6px_0px_#00000033]">
        <div className="flex items-center gap-4">
          <p className="text-lg font-bold text-primary">契約情報</p>
        </div>
        <div className="mt-7 flex flex-col gap-7">
          {isCompanyProfileEdit ? (
            <form className="flex flex-col gap-y-md">
              <div className="grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-[30px]">
                <CompanyProfileData
                  label="契約状況"
                  text={watchField('contract_status') || companyContract?.contract_status || ''}
                  isEditing={isCompanyProfileEdit}
                  register={registerField}
                  name="contract_status"
                  fieldType="single_select"
                  options={CONTRACT_STATUS_ITEMS.map((item, index) => ({
                    id: index + 1,
                    value: item.value,
                    label: item.label,
                  }))}
                  watch={watchField}
                />
                <CompanyProfileData
                  label="手数料率"
                  text={watchField('fee_rate') || companyContract?.display_fee_rate || ''}
                  isEditing={isCompanyProfileEdit}
                  register={registerField}
                  name="fee_rate"
                  fieldType="number"
                  watch={watchField}
                  unit="%"
                />
              </div>
              <div className="grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-[30px]">
                <CompanyProfileData
                  label="ミニマムフィー"
                  text={watchField('minimum_fee') || companyContract?.display_minimum_fee || ''}
                  isEditing={isCompanyProfileEdit}
                  register={registerField}
                  name="minimum_fee"
                  fieldType="number"
                  watch={watchField}
                />
                <div />
              </div>
              <div className="w-full">
                <CompanyProfileData
                  label="備考"
                  text={watchField('note') || companyContract?.note || ''}
                  isEditing={isCompanyProfileEdit}
                  register={registerField}
                  name="note"
                  fieldType="long_text"
                  watch={watchField}
                />
              </div>
            </form>
          ) : (
            <>
              <div className="flex gap-x-[30px]">
                <CompanyProfileData
                  label="契約状況"
                  text={getContractStatusValue(companyContract?.contract_status) || ''}
                  isEditing={isCompanyProfileEdit}
                  register={registerField}
                  name="contract_status"
                  fieldType="single_select"
                  options={CONTRACT_STATUS_ITEMS.map((item, index) => ({
                    id: index + 1,
                    value: item.value,
                    label: item.label,
                  }))}
                  watch={watchField}
                />
                <CompanyProfileData
                  label="手数料率"
                  text={companyContract?.display_fee_rate || ''}
                  isEditing={isCompanyProfileEdit}
                  register={registerField}
                  name="fee_rate"
                  fieldType="short_text"
                  watch={watchField}
                />
              </div>
              <div className="flex gap-x-[30px]">
                <CompanyProfileData
                  label="ミニマムフィー"
                  text={companyContract?.display_minimum_fee || ''}
                  isEditing={isCompanyProfileEdit}
                  register={registerField}
                  name="minimum_fee"
                  fieldType="short_text"
                  watch={watchField}
                />
                <div className="w-full" />
              </div>
              <div className="w-full">
                <CompanyProfileData
                  label="備考"
                  text={companyContract?.note || ''}
                  isEditing={isCompanyProfileEdit}
                  register={registerField}
                  name="note"
                  fieldType="long_text"
                  watch={watchField}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* エージェント項目セクション */}
      {agentCustomFields.map((category) => (
        <div
          key={category.categoryId}
          className="bg-white px-10 pt-8 pb-9 shadow-[0px_0px_6px_0px_#00000033]"
        >
          <div className="flex items-center gap-4">
            <p className="text-lg font-bold text-primary">{category.categoryName}</p>
          </div>
          <div className="mt-7 flex flex-col gap-7">
            {isCompanyProfileEdit ? (
              <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="flex flex-col gap-y-md">
                {(() => {
                  let skipNext = false;
                  return category.customFields.map((field, index) => {
                    if (skipNext) {
                      skipNext = false;
                      return null;
                    }

                    const fieldValue =
                      watch(`customData.${field.fieldId}`) ||
                      customData?.find((data) => data.custom_field.id === field.fieldId)?.content ||
                      '';

                    if (field.fieldType === 'long_text') {
                      return (
                        <div key={field.fieldId} className="w-full">
                          <CompanyProfileData
                            label={field.label}
                            text={fieldValue}
                            isEditing={isCompanyProfileEdit}
                            register={registerField}
                            name={`customData.${field.fieldId}`}
                            fieldType={field.fieldType}
                            options={field.options}
                            watch={watchField}
                          />
                        </div>
                      );
                    } else {
                      const nextField =
                        index + 1 < category.customFields.length
                          ? category.customFields[index + 1]
                          : null;
                      const isNextShort = nextField && nextField.fieldType !== 'long_text';

                      const nextFieldValue = nextField
                        ? watch(`customData.${nextField.fieldId}`) ||
                          customData?.find((data) => data.custom_field.id === nextField.fieldId)
                            ?.content ||
                          ''
                        : '';

                      if (isNextShort) {
                        skipNext = true;
                      }

                      return (
                        <div
                          key={`row-${index}`}
                          className="grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-[30px]"
                        >
                          <CompanyProfileData
                            label={field.label}
                            text={fieldValue}
                            isEditing={isCompanyProfileEdit}
                            register={registerField}
                            name={`customData.${field.fieldId}`}
                            fieldType={field.fieldType}
                            options={field.options}
                            watch={watchField}
                          />
                          {isNextShort && (
                            <CompanyProfileData
                              label={nextField.label}
                              text={nextFieldValue}
                              isEditing={isCompanyProfileEdit}
                              register={registerField}
                              name={`customData.${nextField.fieldId}`}
                              fieldType={nextField.fieldType}
                              options={nextField.options}
                              watch={watchField}
                            />
                          )}
                        </div>
                      );
                    }
                  });
                })()}
              </form>
            ) : (
              <>
                {(() => {
                  let skipNext = false;
                  return category.customFields.map((field, index) => {
                    if (skipNext) {
                      skipNext = false;
                      return null;
                    }

                    const fieldValue =
                      customData?.find((data) => data.custom_field.id === field.fieldId)?.content ||
                      '';

                    if (field.fieldType === 'long_text') {
                      return (
                        <div key={field.fieldId} className="w-full">
                          <CompanyProfileData
                            label={field.label}
                            text={fieldValue}
                            isEditing={isCompanyProfileEdit}
                            register={registerField}
                            name={`customData.${field.fieldId}`}
                            fieldType={field.fieldType}
                            options={field.options}
                            watch={watchField}
                          />
                        </div>
                      );
                    } else {
                      const nextField =
                        index + 1 < category.customFields.length
                          ? category.customFields[index + 1]
                          : null;
                      const isNextShort = nextField && nextField.fieldType !== 'long_text';

                      const nextFieldValue = nextField
                        ? customData?.find((data) => data.custom_field.id === nextField.fieldId)
                            ?.content || ''
                        : '';

                      if (isNextShort) {
                        skipNext = true;
                      }

                      return (
                        <div key={`row-${index}`} className="flex gap-x-[30px]">
                          <CompanyProfileData
                            label={field.label}
                            text={fieldValue}
                            isEditing={isCompanyProfileEdit}
                            register={registerField}
                            name={`customData.${field.fieldId}`}
                            fieldType={field.fieldType}
                            options={field.options}
                            watch={watchField}
                          />
                          {isNextShort && (
                            <CompanyProfileData
                              label={nextField.label}
                              text={nextFieldValue}
                              isEditing={isCompanyProfileEdit}
                              register={registerField}
                              name={`customData.${nextField.fieldId}`}
                              fieldType={nextField.fieldType}
                              options={nextField.options}
                              watch={watchField}
                            />
                          )}
                        </div>
                      );
                    }
                  });
                })()}
              </>
            )}
          </div>
        </div>
      ))}

      {isCompanyProfileEdit && (
        <div className="fixed right-6 bottom-6 w-[96px]">
          <Button
            className="h-[52px]"
            onClick={handleSubmit(onSubmit, onInvalid)}
            disabled={!isFormValid}
          >
            保存する
          </Button>
        </div>
      )}
      {isImportModalOpen && suggestionInfo && (
        <CompaniesImportModal
          companyId={companyId}
          modal={{ id: 'companies-import-modal', isShow: isImportModalOpen }}
          suggestionInfo={suggestionInfo}
          close={() => setIsImportModalOpen(false)}
          companyRefetch={
            companyRefetch as (
              options?: RefetchOptions
            ) => Promise<QueryObserverResult<AgentApiCompaniesShow, Error>>
          }
        />
      )}
    </div>
  );
}
